import React, { useRef, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { useFlexibleMemoizedCallback } from 'pxc-editor-core/helpers/reactHooks';
import {
  TEXT_TAB, CODE_TAB, SYMBOL_TAB, IMAGE_TAB, MATRIX_TAB,
  SELECTION_TOOL,
  ZOOM_CUSTOM_SCALE_SEED_COEFICIENT, USER_SIZE_UNIT_INCH, ENDLESS_FIXED,
  USER_DEVICE_MANIPULATOR_TYPE
} from '@/constants';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import { getPaginationPageItemsByPage, mmToInch, isWebComponentEnvironment } from '@/helpers';
import {
  manuallyScaleSchema,
  handleShapeClick,
  moveSection,
  resizeSection,
  handlePageClick,
  handlePageDragEnd,
  handleFreeSpaceClick,
  handleZoomClick,
  setEditorMenuTab,
  setIsCableFlagSyncState,
  setWizardData
} from '@/actions';
import { colors } from '@/globalStyles';
import { SchemaSvgRenderingAreaStyled } from './styles.js';

const renderingColors = { pageSelectionFrame: colors.primary };

const SchemaSvgRenderingAreaContainer = (props) => {
  const {
    pagesOrder,
    paginationPageNum,
    manuallyScaleSchema,
    userSizeUnit,
    userDeviceManipulatorType,
    handlePageDragEnd,
    handleFreeSpaceClick,
    handleZoomClick,
    openTextEditorMenuTab,
    openSymbolEditorMenuTab,
    openCodeEditorMenuTab,
    openImageEditorMenuTab,
    openMatrixEditorMenuTab,
    onAfterSectionDblClick,
    selWizard,
    isZoomAvailable,
    ...restProps
    // ***outside props *** width, height, onAfterSectionDblClick
  } = props;
  const intl = useIntl();
  const zoomingWithoutScaleCenter = useRef(null);
  const zoomingPrevDist = useRef(0);
  const visiblePagesUids = useMemo(() => getPaginationPageItemsByPage(pagesOrder, paginationPageNum), [pagesOrder, paginationPageNum]);

  const buildPageLengthLabelHelper = useCallback((pageWidth, pageResizeType) => {
    if (pageResizeType === ENDLESS_FIXED) {
      const userSizeUnitTransl = intl.formatMessage({id: `app.sizeUnits.${userSizeUnit}`, defaultMessage: userSizeUnit});
      return `${userSizeUnit === USER_SIZE_UNIT_INCH ? mmToInch(pageWidth, true) : pageWidth} ${userSizeUnitTransl}`;
    }
    else {
      return Infinity;
    }
  }, [userSizeUnit]);

  const onContentDragEnd = useCallback(({dx, dy}) => {
    handlePageDragEnd(dx, dy);
  }, []);
  const onContentClick = useCallback(ev => {
    if (!ev.activeElem && !ev.target.closest('#page-length-control')) {
      handleFreeSpaceClick();
    }
  }, []);
  const onContentDblClick = useFlexibleMemoizedCallback(ev => {
    if (!ev.activeElem) {
      handleZoomClick();
    }
  });

  const onZoomingEnd = useCallback(ev => {
    zoomingPrevDist.current = 0;
    zoomingWithoutScaleCenter.current = null;
  }, []);
  // zooming by fingers touch
  const onZooming = useFlexibleMemoizedCallback(ev => {
    if (isZoomAvailable) {
      if (zoomingWithoutScaleCenter.current === null) {
        // memorize start zooming center position when scaleCoef=1 and viewbox=0,0
        zoomingWithoutScaleCenter.current = {
          x: (ev.zoomingStartCenter.x + props.viewboxX) / props.scaleCoef,
          y: (ev.zoomingStartCenter.y + props.viewboxY) / props.scaleCoef,
        };
        zoomingPrevDist.current = ev.zoomingDistance;
      }
  
      const newCoefStep = (ev.zoomingDistance - zoomingPrevDist.current) * getZoomScaleSpeedCoef(props.scaleCoef);
  
      if (newCoefStep) { // if not 0
        manuallyScaleSchema((props.scaleCoef + newCoefStep), zoomingWithoutScaleCenter.current);
      }
  
      zoomingPrevDist.current = ev.zoomingDistance;
    }
  }, [isZoomAvailable]);

  const webComponent = isWebComponentEnvironment();
  const shieldsSelectionByDragRect = webComponent || props.cellSelectionType === SELECTION_TOOL;
  const matfileTemplate = getBuildedMatfileTemplate();

  return (
    <SchemaSvgRenderingAreaStyled
      {...restProps}
      mode={props.mode}
      visiblePagesUids={visiblePagesUids}
      materialType={matfileTemplate?.type || undefined}
      materialPrintParameters={matfileTemplate?.printParameters || undefined}
      zoomingContent={true}
      isUserManipulatorTouch={userDeviceManipulatorType === USER_DEVICE_MANIPULATOR_TYPE.TOUCH}
      shieldsSelectionByDragRect={shieldsSelectionByDragRect}
      colors={renderingColors}
      //onDrag
      buildPageLengthLabelHelper={buildPageLengthLabelHelper}
      onSectionDblClick={onAfterSectionDblClick}
      onContentDragEnd={onContentDragEnd}
      onContentClick={onContentClick}
      onContentDblClick={onContentDblClick}
      onZooming={onZooming}
      onZoomingEnd={onZoomingEnd}
      selWizard={selWizard}
      userSizeUnit={userSizeUnit}
    />
  );
};

const mapStateToProps = state => ({
  pages: state.core.pages,
  pagesSelection: state.core.pagesSelection,
  viewboxX: state.core.viewboxX,
  viewboxY: state.core.viewboxY,
  mode: state.core.mode,
  shieldsSelection: state.core.shieldsSelection,
  sectionsSelection: state.core.sectionsSelection,
  isMultiColored: state.core.isMultiColored,
  isGridDisplayed: state.core.isGridDisplayed,
  gridWidth: state.core.gridWidth,
  scaleCoef: state.core.scaleCoef,
  isContentPlacingDebugging: state.core.isContentPlacingDebugging,
  isDebuggingGrid: state.core.isDebuggingGrid,
  userSizeUnit: state.core.userSizeUnit,
  fictiveSections: state.core.fictiveSections,
  cellSelectionType: state.core.cellSelectionType,
  userDeviceManipulatorType: state.core.userDeviceManipulatorType,
  //*** in current component
  paginationPageNum: state.core.paginationPageNum,
  pagesOrder: state.core.pagesOrder,
  selWizard: state.core.selWizard,
});

const mapDispatchToProps = dispatch => ({
  moveSection: (id, { dx, dy }) => dispatch(moveSection(id, { dx, dy })),
  resizeSection: (id, margins) => dispatch(resizeSection(id, margins)),
  handlePageClick: pageUid => dispatch(handlePageClick(pageUid)),
  onShapeClick: shape => dispatch(handleShapeClick(shape, true)),
  setIsCableFlagSyncState: state => dispatch(setIsCableFlagSyncState(state)),
  setWizardData: data => dispatch(setWizardData(data)),
  //*** in current component
  openTextEditorMenuTab: () => dispatch(setEditorMenuTab(TEXT_TAB)),
  openSymbolEditorMenuTab: () => dispatch(setEditorMenuTab(SYMBOL_TAB)),
  openCodeEditorMenuTab: () => dispatch(setEditorMenuTab(CODE_TAB)),
  openImageEditorMenuTab: () => dispatch(setEditorMenuTab(IMAGE_TAB)),
  openMatrixEditorMenuTab: () => dispatch(setEditorMenuTab(MATRIX_TAB)),
  handleFreeSpaceClick: () => dispatch(handleFreeSpaceClick()),
  handlePageDragEnd: (dx, dy) => dispatch(handlePageDragEnd(dx, dy)),
  manuallyScaleSchema: (newScaleCoef, scaleCenter) => dispatch(manuallyScaleSchema(newScaleCoef, scaleCenter)),
  handleZoomClick: () => dispatch(handleZoomClick()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SchemaSvgRenderingAreaContainer);

const getZoomScaleSpeedCoef = (currScaleCoef) => {
  if (currScaleCoef > 10) {
    return 0.1;
  }
  else if (currScaleCoef > 0.1) {
    return currScaleCoef * ZOOM_CUSTOM_SCALE_SEED_COEFICIENT;
  }
  else {
    return 0.001;
  }
};